import { forwardRef, useImperativeHandle, useState } from "react";
import { toast } from "sonner";
import { apiClient, getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ADMIN } from "@/constants/testIds";
import { ReportFormFields } from "@/pages/admin/ReportFormFields";

const EMPTY_FORM = {
  company_name: "", symbol: "", sector: "", industry: "", kira_score: "", recommendation: "Hold",
  report_date: "", current_price: "", market_cap: "",
  summary: "", highlights_text: "", strengths_text: "", risks_text: "", metrics_text: "",
  pdf_media_id: null, logo_media_id: null, cover_media_id: null,
};

function parseLines(t) {
  return t.split("\n").map((l) => l.trim()).filter(Boolean);
}

function parseMetrics(t) {
  return t.split("\n").map((line) => {
    const [label, ...valueParts] = line.split("|");
    const value = valueParts.join("|").trim();
    const cleanLabel = (label || "").trim();
    if (!cleanLabel || !value) return null;
    return { label: cleanLabel, value };
  }).filter(Boolean);
}

/**
 * Create/edit report dialog for a section. Fully self-contained (owns its own
 * open/form/saving/upload state) — parent triggers it imperatively via ref:
 *   formRef.current.openCreate() / formRef.current.openEdit(report)
 */
export const ReportFormDialog = forwardRef(function ReportFormDialog({ sectionKey, sectionLabel, onSaved }, ref) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploads, setUploads] = useState({});

  useImperativeHandle(ref, () => ({
    openCreate: () => {
      setEditing(null);
      setForm({ ...EMPTY_FORM, report_date: new Date().toISOString().slice(0, 10) });
      setDialogOpen(true);
    },
    openEdit: (r) => {
      setEditing(r);
      setForm({
        company_name: r.company_name, symbol: r.symbol || "", sector: r.sector, industry: r.industry || "",
        kira_score: String(r.kira_score), recommendation: r.recommendation || "Hold",
        report_date: r.report_date,
        current_price: r.current_price || "",
        market_cap: r.market_cap || "",
        summary: r.summary || "",
        highlights_text: (r.key_highlights || []).join("\n"),
        strengths_text: (r.strengths || []).join("\n"),
        risks_text: (r.risks || []).join("\n"),
        metrics_text: (r.key_metrics || []).map((m) => `${m.label} | ${m.value}`).join("\n"),
        pdf_media_id: r.pdf_media_id, logo_media_id: r.logo_media_id, cover_media_id: r.cover_media_id,
      });
      setDialogOpen(true);
    },
  }));

  const handleUpload = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploads((u) => ({ ...u, [field]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.id }));
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setUploads((u) => ({ ...u, [field]: false }));
      e.target.value = "";
    }
  };

  const save = async (status) => {
    if (!form.company_name || !form.sector || form.kira_score === "" || !form.report_date) {
      toast.error("Company, sector, KIRA Score and report date are required.");
      return;
    }
    const score = Number(form.kira_score);
    if (Number.isNaN(score) || score < 0 || score > 100) {
      toast.error("KIRA Score must be a number between 0 and 100.");
      return;
    }
    setSaving(true);
    const { highlights_text, strengths_text, risks_text, metrics_text, ...rest } = form;
    const payload = {
      ...rest,
      symbol: form.symbol.trim().toUpperCase() || null,
      industry: form.industry.trim() || null,
      current_price: form.current_price.trim() || null,
      market_cap: form.market_cap.trim() || null,
      key_highlights: parseLines(highlights_text),
      strengths: parseLines(strengths_text),
      risks: parseLines(risks_text),
      key_metrics: parseMetrics(metrics_text),
      kira_score: score,
      section: sectionKey,
      status,
    };
    try {
      if (editing) {
        await apiClient.put(`/admin/reports/${editing.id}`, payload);
        toast.success(status === "published" ? "Report updated & published" : "Draft saved");
      } else {
        await apiClient.post("/admin/reports", payload);
        toast.success(status === "published" ? `${form.company_name} is live on the site` : "Draft saved");
      }
      setDialogOpen(false);
      onSaved();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit — ${editing.company_name}` : `New Report — ${sectionLabel}`}</DialogTitle>
          <DialogDescription>Fill the report details and publish — it goes live on the site instantly.</DialogDescription>
        </DialogHeader>
        <ReportFormFields form={form} setForm={setForm} uploads={uploads} onUpload={handleUpload} />
        <DialogFooter className="gap-2">
          <Button data-testid={ADMIN.formDraftButton} variant="outline" onClick={() => save("draft")} disabled={saving}>
            Save Draft
          </Button>
          <Button data-testid={ADMIN.formPublishButton} onClick={() => save("published")} disabled={saving}
            className="bg-gray-900 hover:bg-gray-800">
            {saving ? "Saving..." : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
